import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useAutoReplies, useSaveAutoReplies, type AutoReplies, type BusinessDay, type KeywordRule } from '../api/autoReplies'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

const dayNames: Record<BusinessDay['day'], string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
}

const zones = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'UTC']

// Automatic replies. Everyone sees what customers receive; owners and admins
// change it, and the server refuses everyone else whatever this page shows.
export function AutoRepliesPage() {
  const me = useMe()
  const settings = useAutoReplies()
  // Owned here, not by the editor, so "Saved" survives the editor resetting to the saved version.
  const save = useSaveAutoReplies()
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'
  // The version the editor started from. A teammate's save arrives live; the
  // editor moves to it unless that would throw away unsaved edits.
  const [base, setBase] = useState<AutoReplies | undefined>(undefined)
  // Pinned on first load (React's adjust-state-while-rendering pattern).
  if (base === undefined && settings.data) setBase(settings.data)

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Auto-replies</h1>
        <p className="text-sm text-muted-foreground">
          Answer customers when nobody can. A customer gets at most one auto-reply per message, the same one at most once a
          day, and none while a teammate has replied in the last 30 minutes. Auto-replies are service messages: they count
          toward each number's free service messages for the month, shown on the inbox meter, and past those Meta bills
          the utility rate.
        </p>
      </div>
      {settings.isPending ? (
        <div role="status" aria-label="Loading auto-replies" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : settings.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(settings.error as RequestFailed).message}</p>
      ) : (
        // Keyed by the version it started from, so adopting a newer one resets the draft.
        <Editor
          key={canonical(base ?? settings.data)}
          saved={base ?? settings.data}
          latest={settings.data}
          onAdopt={() => setBase(settings.data)}
          admin={admin}
          save={save}
        />
      )}
    </main>
  )
}

// A keyword rule while it is being edited: keywords as typed, comma separated.
type DraftRule = { id?: string; keywords: string; text: string; enabled: boolean; key: number }

let nextKey = 0

// JSON with object keys sorted, so a draft equals the saved document however
// either one ordered its fields.
function canonical(v: unknown): string {
  return JSON.stringify(v, (_, x: unknown) =>
    x && typeof x === 'object' && !Array.isArray(x)
      ? Object.fromEntries(Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : x,
  )
}

function toDraft(r: KeywordRule): DraftRule {
  const d: DraftRule = { keywords: r.keywords.join(', '), text: r.text, enabled: r.enabled, key: nextKey++ }
  if (r.id) d.id = r.id
  return d
}

function fromDraft(r: DraftRule): KeywordRule {
  const out: KeywordRule = { keywords: r.keywords.split(',').map((k) => k.trim()).filter(Boolean), text: r.text, enabled: r.enabled }
  if (r.id) out.id = r.id
  return out
}

function Editor({
  saved,
  latest,
  onAdopt,
  admin,
  save,
}: {
  saved: AutoReplies
  latest: AutoReplies
  onAdopt: () => void
  admin: boolean
  save: ReturnType<typeof useSaveAutoReplies>
}) {
  const [doc, setDoc] = useState(saved)
  const [rules, setRules] = useState(() => saved.keywords.map(toDraft))
  const body: AutoReplies = { ...doc, keywords: rules.map(fromDraft) }
  const dirty = canonical(body) !== canonical(saved)
  const ro = !admin
  // A newer saved version: our own save, or a teammate's. Move to it when
  // nothing typed here would be lost; otherwise say so and let the user choose.
  const newer = canonical(latest) !== canonical(saved)
  // Our own save may come back normalised (keywords trimmed, lower case), so
  // it is recognised by the server's answer, not by comparing with the draft.
  const ours = save.data !== undefined && canonical(save.data) === canonical(latest)
  const conflict = newer && dirty && !ours && canonical(body) !== canonical(latest)
  useEffect(() => {
    if (newer && !conflict) onAdopt()
  }, [newer, conflict, onAdopt])

  const setDay = (i: number, change: Partial<BusinessDay>) =>
    setDoc((d) => ({ ...d, hours: d.hours.map((h, j) => (j === i ? { ...h, ...change } : h)) }))
  const setRule = (key: number, change: Partial<DraftRule>) =>
    setRules((rs) => rs.map((r) => (r.key === key ? { ...r, ...change } : r)))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate(body)
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={submit}>
      {ro && (
        <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">Only owners and admins can change auto-replies.</p>
      )}
      {conflict && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-lg bg-attention-soft px-3 py-2 text-sm text-attention-text">
          <p className="flex-[1_1_240px]">A teammate saved auto-replies while you were editing. Saving yours replaces their changes.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => { save.reset(); onAdopt() }}>
            Load their version
          </Button>
        </div>
      )}
      <fieldset disabled={ro} className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0">
        <Section title="Away message" hint="Sent when a customer writes outside business hours.">
          <Toggle
            id="away-on"
            label="Send the away message"
            checked={doc.away.enabled}
            onChange={(on) => setDoc({ ...doc, away: { ...doc.away, enabled: on } })}
          />
          <Textarea aria-label="Away message" rows={3} maxLength={1024} value={doc.away.text} onChange={(e) => setDoc({ ...doc, away: { ...doc.away, text: e.target.value } })} />
        </Section>

        <Section title="Business hours">
          <div className="flex flex-col gap-2">
            <Label htmlFor="tz">Time zone</Label>
            <Input id="tz" list="zones" className="max-w-xs" value={doc.timezone} onChange={(e) => setDoc({ ...doc, timezone: e.target.value })} />
            <datalist id="zones">{zones.map((z) => <option key={z} value={z} />)}</datalist>
          </div>
          <ul className="flex flex-col divide-y">
            {doc.hours.map((h, i) => (
              <li key={h.day} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2">
                <div className="flex w-36 items-center gap-2">
                  <Checkbox id={`day-${h.day}`} checked={h.open} onCheckedChange={(c) => setDay(i, { open: c === true })} />
                  <Label htmlFor={`day-${h.day}`} className="font-normal">{dayNames[h.day]}</Label>
                </div>
                {h.open ? (
                  <span className="flex items-center gap-2">
                    <Input type="time" className="w-32 tabular-nums" aria-label={`${dayNames[h.day]} opens`} value={h.from} onChange={(e) => setDay(i, { from: e.target.value })} />
                    <span className="text-sm text-muted-foreground">to</span>
                    <Input type="time" className="w-32 tabular-nums" aria-label={`${dayNames[h.day]} closes`} value={h.to} onChange={(e) => setDay(i, { to: e.target.value })} />
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Closed</span>
                )}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Welcome message" hint="Sent on a customer's first message, or the first after 7 days of silence.">
          <Toggle
            id="welcome-on"
            label="Send the welcome message"
            checked={doc.welcome.enabled}
            onChange={(on) => setDoc({ ...doc, welcome: { ...doc.welcome, enabled: on } })}
          />
          <Textarea aria-label="Welcome message" rows={3} maxLength={1024} value={doc.welcome.text} onChange={(e) => setDoc({ ...doc, welcome: { ...doc.welcome, text: e.target.value } })} />
        </Section>

        <Section
          title="Keyword replies"
          hint={'Answer a message that contains a keyword as a whole word, any case: "price" matches "What is the PRICE?" but not "priceless". The first matching rule wins and comes before the away and welcome messages.'}
        >
          {rules.length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">No keyword replies yet.</p>
          ) : (
            <ol className="flex flex-col gap-3">
              {rules.map((r, i) => (
                <li key={r.key} className="flex flex-col gap-3 rounded-lg border p-3">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={`kw-${r.key}`}>Keywords, separated by commas</Label>
                    <Input id={`kw-${r.key}`} value={r.keywords} placeholder="price, cost, rate" onChange={(e) => setRule(r.key, { keywords: e.target.value })} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={`kr-${r.key}`}>Reply</Label>
                    <Textarea id={`kr-${r.key}`} rows={2} maxLength={1024} value={r.text} onChange={(e) => setRule(r.key, { text: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Toggle id={`kon-${r.key}`} label="On" checked={r.enabled} onChange={(on) => setRule(r.key, { enabled: on })} />
                    <span className="flex-1" />
                    <Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive" aria-label={`Remove keyword reply ${i + 1}`} onClick={() => setRules((rs) => rs.filter((x) => x.key !== r.key))}>
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
          )}
          {admin && rules.length < 50 && (
            <Button type="button" variant="outline" className="self-start" onClick={() => setRules((rs) => [...rs, { keywords: '', text: '', enabled: true, key: nextKey++ }])}>
              Add keyword reply
            </Button>
          )}
        </Section>
      </fieldset>

      {admin && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t bg-background py-3">
          {save.error && <p role="alert" className="text-sm text-destructive">{(save.error as RequestFailed).message}</p>}
          {save.isSuccess && !dirty && <p className="text-sm text-primary-text" role="status">Saved</p>}
          <span className="flex-1" />
          <Button type="button" variant="ghost" disabled={!dirty || save.isPending} onClick={() => { setDoc(saved); setRules(saved.keywords.map(toDraft)); save.reset() }}>
            Discard changes
          </Button>
          <Button type="submit" disabled={!dirty || save.isPending}>{save.isPending ? 'Saving' : 'Save'}</Button>
        </div>
      )}
    </form>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border bg-card p-4 sm:p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

function Toggle({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id} className="font-normal">{label}</Label>
    </div>
  )
}
